import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import { useForm } from "react-hook-form";
import { Amplify } from 'aws-amplify';
import amplifyconfig from './amplifyconfiguration.json';
import { submitRegistration } from './api';
import LogoOnly from "./LogoOnly";
import ConfirmEmail from "./ConfirmEmail";
import Dashboard from "./Dashboard";
import CheckIn from "./CheckIn";
import { useLanguage } from "./i18n/LanguageContext";
import LanguageSelector from "./components/LanguageSelector";
import CookieConsent from "./components/CookieConsent";
import Footer from "./components/Footer";
import { COUNTRIES } from "./data/countries";
import { DIAL_CODES } from "./data/dialCodes";
import { mapUTMToCategory } from "./utils/utmCategoryMapping";
import { useSkin } from "./skins/useSkin";
import "./skins/us-wireframe.css";
import "./skins/carshow.css";

// US tenant short-circuit: ship the Figma-aligned signup form for any
// mmyt-us* branch and skip the legacy EU layout, language selector,
// country dropdown, dial-code wrapper, etc.
const IS_US = (process.env.REACT_APP_EVENT_NAME || "").startsWith("mmyt-us");

const EVENT_NAME = process.env.REACT_APP_EVENT_NAME;
const UPDATE_POS = process.env.REACT_APP_UPDATE_POS;
const RESTRICT_EMAIL_DOMAINS = process.env.REACT_APP_RESTRICT_EMAIL_DOMAINS === 'true';

// Initialize Amplify - will be configured with actual values after deployment
Amplify.configure(amplifyconfig);

// ========================================
// TOGGLE THIS TO SHOW LOGO ONLY
// Set to true for clean white background with logo
// Set to false to show the full form
const SHOW_LOGO_ONLY = false;
// ========================================

// Check URL parameters before component mounts
const params = new URLSearchParams(window.location.search);
const onsite = params.get('onsite');
if (onsite === 'true' || onsite === 'false') {
  localStorage.setItem('onsite', onsite);
}

// Utility function to build Shop URL with UTM forwarding
const buildShopUrl = () => {
  const baseUrl = 'https://www.mytheresa.com/to/women/edits/the-maison-mytheresa-edit';
  const currentParams = new URLSearchParams(window.location.search);

  // Check if any UTM/campaign params exist in current URL
  const hasCampaignParams = currentParams.has('utm_source') ||
                            currentParams.has('utm_campaign') ||
                            currentParams.has('utm_medium');

  if (hasCampaignParams) {
    // Forward ALL incoming params to destination URL
    const targetUrl = new URL(baseUrl);
    for (const [key, value] of currentParams) {
      targetUrl.searchParams.set(key, value);
    }
    return targetUrl.toString();
  } else {
    // Use original default params when no campaign params present
    return `${baseUrl}?utm_source=yacht&utm_campaign=maison-yacht-2026`;
  }
};

// Utility function to get referral source with fallback hierarchy
const getReferralSource = () => {
  const urlParams = new URLSearchParams(window.location.search);

  // Capture click IDs (present regardless of UTM params)
  const gclid = urlParams.get('gclid') || '';
  const fbclid = urlParams.get('fbclid') || '';

  // Priority 1: Explicit UTM parameters
  if (urlParams.get('utm_source')) {
    const params = {
      source: urlParams.get('utm_source'),
      medium: urlParams.get('utm_medium') || '',
      campaign: urlParams.get('utm_campaign') || '',
    };
    return {
      ...params,
      method: 'utm_params',
      category: mapUTMToCategory(params, 'utm_params'),
      gclid,
      fbclid,
    };
  }

  // Priority 2: Parse referrer URL
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      const hostname = referrerUrl.hostname;

      let params;
      // Map common referrers
      if (hostname.includes('facebook.com') || hostname.includes('fb.com')) {
        params = { source: 'facebook', medium: 'referral', campaign: '' };
      } else if (hostname.includes('google.')) {
        params = { source: 'google', medium: 'organic', campaign: '' };
      } else if (hostname.includes('instagram.com')) {
        params = { source: 'instagram', medium: 'referral', campaign: '' };
      } else {
        params = { source: hostname, medium: 'referral', campaign: '' };
      }
      return { ...params, method: 'referrer', category: mapUTMToCategory(params, 'referrer'), gclid, fbclid };
    } catch {
      // Invalid referrer URL, fall through to direct
    }
  }

  // Priority 3: Direct/unknown
  const params = { source: 'direct', medium: 'none', campaign: '' };
  return { ...params, method: 'unknown', category: mapUTMToCategory(params, 'unknown'), gclid, fbclid };
};

function App() {
  useSkin();
  const { t, translations, language } = useLanguage();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const [submitted, setSubmitted] = useState(
    new URLSearchParams(window.location.search).get('preview') === 'success'
  );
  const [firstname, setFirstName] = useState("");
  const [emailProvidersVisible, setEmailProvidersVisible] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [submitError, setSubmitError] = useState(false);
  const emailRef = useRef(null);

  // Check if user is onsite
  const isOnsite = localStorage.getItem("onsite") === "true";

  // Track page view on mount with referral data for channel attribution
  useEffect(() => {
    const trackPageView = async () => {
      try {
        // Get pageview API URL from amplify config
        const pageviewUrl = amplifyconfig.custom?.pageviewApiUrl;
        if (pageviewUrl) {
          // Include referral data for marketing channel tracking
          const referralData = getReferralSource();
          await fetch(pageviewUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pageId: 'registration',
              referral: {
                source: referralData.source,
                medium: referralData.medium,
                campaign: referralData.campaign,
                method: referralData.method,
                category: referralData.category,
              },
            }),
          });
        }
      } catch (error) {
        // Silently fail - page view tracking should not affect user experience
        console.debug('Page view tracking failed:', error);
      }
    };
    trackPageView();
  }, []);

  const emailProviders = [
    "@gmail.com",
    "@yahoo.com",
    "@icloud.com",
    "@me.com",
    "@hotmail.com",
    "@aol.com",
  ];

  // If SHOW_LOGO_ONLY is true, render only the logo
  if (SHOW_LOGO_ONLY) {
    return (
      <>
        <LogoOnly />
        <CookieConsent translations={translations} />
      </>
    );
  }

  const onSubmit = async (data) => {
    setFirstName(data.firstname.trim());
    setSubmitError(false);
    data.event = EVENT_NAME;
    data.updatePOS = UPDATE_POS;
    data.onsite = isOnsite;
    data.language = language; // Pass user's language for confirmation email
    data.referral = getReferralSource(); // Add referral source tracking

    try {
      // US prod Aurora auto-pauses when idle; the first request during
      // resume fails with a 500 (DatabaseResumingException) and succeeds
      // ~10-15s later. Retry twice with backoff so cold-DB visitors don't
      // lose their registration.
      let response = await submitRegistration(data);
      for (const delayMs of [5000, 8000]) {
        if (response.ok) break;
        await new Promise((r) => setTimeout(r, delayMs));
        response = await submitRegistration(data);
      }

      if (response.ok) {
        // EBS /prospects returns { id, createdAt, updatedAt } only — no
        // confirmRequired / isNewUser flags. DACH double-opt-in messaging is
        // deferred to a later slice; for now everyone sees the same direct
        // thank-you. confirmationResult kept as a tombstone so getSuccessMessage
        // can stay backward-compatible if we ever reintroduce per-flag copy.
        setConfirmationResult(null);
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          setSubmitted(false);
          setConfirmationResult(null);
          reset();
        }, isOnsite ? 10000 : 10000);
      } else {
        console.error("Failed to submit data to the prospects API");
        setSubmitError(true);
      }
    } catch (error) {
      console.error("An error occurred", error);
      setSubmitError(true);
    }
  };

  const handleBlur = (field) => {
    const value = document.getElementById(field).value.trim();
    setValue(field, value);
  };

  const formatPhoneNumber = (event) => {
    const input = event.target.value.replace(/\D/g, "").substring(0, 10);
    const areaCode = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);

    if (input.length > 6) {
      event.target.value = `(${areaCode}) ${middle} - ${last}`;
    } else if (input.length > 3) {
      event.target.value = `(${areaCode}) ${middle}`;
    } else if (input.length > 0) {
      event.target.value = `(${areaCode}`;
    }

    setValue("phone", event.target.value);
  };

  const handleFocus = (field) => {
    if (field === "email") {
      setEmailProvidersVisible(false);
    } else {
      setEmailProvidersVisible(false);
    }
  };

  const handleEmailInput = () => {
    setEmailProvidersVisible(false);
  };

  const handleEmailProviderClick = (provider) => {
    const emailInput = emailRef.current;
    const value = emailInput.value.trim();
    const atIndex = value.indexOf("@");
    const newValue =
      atIndex !== -1
        ? value.substring(0, atIndex) + provider
        : value + provider;
    emailInput.value = newValue;
    setValue("email", newValue);
    setEmailProvidersVisible(false);
  };

  // Helper function to render disclaimer with privacy policy link
  const renderDisclaimerText = () => {
    const disclaimerText = t('disclaimers.marketing');
    const privacyPolicyUrl = t('disclaimers.privacyPolicyUrl');
    const privacyPolicyText = t('disclaimers.privacyPolicyText');

    const parts = disclaimerText.split('{privacyPolicyLink}');

    return (
      <>
        {parts[0]}
        <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
          {privacyPolicyText}
        </a>
        {parts[1]}
      </>
    );
  };

  // Check if we're on the confirmation page
  const isConfirmPage = window.location.pathname === '/confirm' ||
                        window.location.search.includes('token=');

  // Check if we're on the dashboard page
  const isManagePage = window.location.pathname === '/manage';

  // Check if we're on the check-in page
  const isCheckinPage = window.location.pathname === '/checkin';

  // If on confirmation page, render ConfirmEmail component
  if (isConfirmPage) {
    return <ConfirmEmail />;
  }

  // If on manage page, render Dashboard component
  if (isManagePage) {
    return <Dashboard />;
  }

  // If on check-in page, render CheckIn component
  if (isCheckinPage) {
    return <CheckIn />;
  }

  // Show LogoOnly if SHOW_LOGO_ONLY is true
  if (SHOW_LOGO_ONLY) {
    return <LogoOnly />;
  }

  // Determine which success message to show.
  // EBS /prospects doesn't issue confirmation tokens or distinguish new-vs-
  // returning users, so we show the unified non-DACH thank-you for everyone.
  // The DACH double-opt-in flow (and the dachNew/dachExisting copy) will return
  // when the downstream consumer is rewired off Amplify — handled in a
  // separate slice.
  const getSuccessMessage = () => {
    if (confirmationResult?.confirmRequired) {
      return confirmationResult.isNewUser
        ? t('success.dachNew')
        : t('success.dachExisting');
    }
    return t('success.nonDach');
  };

  if (submitted) {
    if (IS_US) {
      return (
        <div className="mt-shell">
          <header className="mt-header">
            <img
              src="/images/carshow/mytheresa-wordmark.png"
              alt="Mytheresa"
              className="mt-wordmark"
            />
          </header>
          <main className="mt-main">
            <div className="mt-success">
              <h1 className="mt-title">Thank You.</h1>
              <p className="mt-subtitle">
                Your registration is confirmed. A confirmation email has been sent to your inbox.
              </p>
              <p className="mt-promo">
                From September 1st until October 19th, 10% of sales using code
                THEBRIDGE at checkout will be donated to The Bridge Golf
                Foundation. Shop our dedicated{" "}
                {/* URL as supplied by client (de/en storefront); confirm us/en before launch */}
                <a
                  href="https://www.mytheresa.com/de/en/men/edits/the-bridge"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  The Bridge Edit
                </a>{" "}
                now.
              </p>
            </div>
          </main>
          <CookieConsent translations={translations} />
        </div>
      );
    }

    const successMsg = getSuccessMessage();

    return (
      <>
        <div className="language-selector-wrapper">
          <LanguageSelector />
        </div>
        <div className="content-container">
          <header className="App-header">
            <div className="success-message">
              <p>{successMsg}</p>
            </div>
            <button
              type="button"
              onClick={() => window.open(buildShopUrl(), '_blank')}
              className="shop-now-button"
            >
              {t('form.shopNow')}
            </button>
          </header>
        </div>
        <Footer />
        <CookieConsent translations={translations} />
      </>
    );
  }

  if (IS_US) {
    return (
      <div className="mt-shell">
        <header className="mt-header">
          <img
            src="/images/carshow/mytheresa-wordmark.png"
            alt="Mytheresa"
            className="mt-wordmark"
          />
        </header>
        <main className="mt-main">
          <h1 className="mt-title">Mytheresa&rsquo;s Exclusive Lounge Experience</h1>
          <p className="mt-dates">
            September 19, 2026 &middot; 2-7PM &middot; The Bridge Car Show
          </p>
          <p className="mt-intro">
            Mytheresa and The Bridge present an immersive men&rsquo;s lounge
            experience. Join us for a curated selection of styles, cigars and
            whiskey, shoe shining, custom embroidery, and more. Register for
            access.
          </p>
          <form className="mt-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="mt-field">
              <label htmlFor="firstname">First Name</label>
              <input
                id="firstname"
                type="text"
                placeholder="Jane"
                className={errors.firstname ? "error" : ""}
                {...register("firstname", {
                  required: true,
                  pattern: /^[A-Za-z\s'-]+$/,
                })}
                onBlur={() => handleBlur("firstname")}
              />
            </div>
            <div className="mt-field">
              <label htmlFor="lastname">Last Name</label>
              <input
                id="lastname"
                type="text"
                placeholder="Doe"
                className={errors.lastname ? "error" : ""}
                {...register("lastname", {
                  required: true,
                  pattern: /^[A-Za-z\s'-]+$/,
                })}
                onBlur={() => handleBlur("lastname")}
              />
            </div>
            <div className="mt-field">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                placeholder="jane@example.com"
                className={errors.email ? "error" : ""}
                {...register("email", {
                  required: true,
                  pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/,
                })}
                onBlur={() => handleBlur("email")}
              />
            </div>
            <div className="mt-field">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                {...register("phone")}
              />
            </div>
            {/* Marketing opt-in must NOT be pre-ticked (CCPA/CAN-SPAM; SOW).
                Key semantics match the Mobile Tour precedent so admin
                reporting reads both events the same way:
                disclaimerChecked1 = terms (required), disclaimerChecked2 =
                marketing opt-in. */}
            <label className="mt-checkbox">
              <input type="checkbox" {...register("disclaimerChecked2")} />
              <span>I would like to receive marketing communications from Mytheresa.</span>
            </label>
            <label className="mt-checkbox">
              <input
                type="checkbox"
                className={errors.disclaimerChecked1 ? "error" : ""}
                {...register("disclaimerChecked1", { required: true })}
              />
              <span>
                I accept the{" "}
                <a
                  href="https://www.mytheresa.com/en-us/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms &amp; Conditions
                </a>{" "}
                and have read the{" "}
                <a
                  href="https://www.mytheresa.com/en-us/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
            {submitError && (
              <p className="mt-error" role="alert">
                We couldn&rsquo;t complete your registration. Please try again
                in a moment.
              </p>
            )}
            <button type="submit" className="mt-submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting…" : "Register"}
            </button>
          </form>
        </main>
        <CookieConsent translations={translations} />
      </div>
    );
  }

  return (
    <>
      <div className="language-selector-wrapper">
        <LanguageSelector />
      </div>
      <div className="content-container">
        <header className="App-header">
            {/* Only show event info if NOT onsite */}
            {!isOnsite && (
              <div className="event-info">
                {t('eventInfo.headline') && t('eventInfo.headline') !== 'eventInfo.headline' && (
                  <p className="headline-line">{t('eventInfo.headline')}</p>
                )}
                {t('eventInfo.dates') && <p className="dates-line">{t('eventInfo.dates')}</p>}
                {t('eventInfo.openingDays') && <p className="opening-days-line">{t('eventInfo.openingDays')}</p>}
                {t('eventInfo.hours') && <p className="hours-line">{t('eventInfo.hours')}</p>}
                {t('eventInfo.location') && <p className="location-line">{t('eventInfo.location')}</p>}
                {t('eventInfo.address') && <p className="address-line">{t('eventInfo.address')}</p>}
              </div>
            )}
            <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <input
              id="firstname"
              type="text"
              placeholder={t('form.firstName')}
              className={errors.firstname ? 'error' : ''}
              {...register("firstname", {
                required: true,
                pattern: /^[A-Za-z\s]+$/,
              })}
              onBlur={() => handleBlur("firstname")}
              onFocus={() => handleFocus("firstname")}
            />
          </div>

          <div className="form-group">
            <input
              id="lastname"
              type="text"
              placeholder={t('form.lastName')}
              className={errors.lastname ? 'error' : ''}
              {...register("lastname", {
                required: true,
                pattern: /^[A-Za-z\s]+$/,
              })}
              onBlur={() => handleBlur("lastname")}
              onFocus={() => handleFocus("lastname")}
            />
          </div>

          <div className="form-group">
            <input
              id="email"
              type="email"
              placeholder={t('form.email')}
              className={errors.email ? 'error' : ''}
              {...register("email", {
                required: true,
                pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/,
                validate: (value) => {
                  if (!RESTRICT_EMAIL_DOMAINS) {
                    return true; // Allow all valid emails
                  }
                  const domain = value.toLowerCase().split('@')[1];
                  return domain === 'mytheresa.com' || domain === 'gmail.com' || 'Only @mytheresa.com or @gmail.com emails are allowed';
                }
              })}
              onBlur={() => handleBlur("email")}
              onFocus={() => handleFocus("email")}
              onInput={handleEmailInput}
              ref={emailRef}
            />
            {emailProvidersVisible && (
              <div className="email-providers">
                {emailProviders.map((provider) => (
                  <button
                    type="button"
                    key={provider}
                    onClick={() => handleEmailProviderClick(provider)}
                  >
                    {provider}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="form-group phone-group">
            <div className="phone-dial-code-wrapper">
              <select
                id="phoneCountry"
                aria-label={t('form.dialCode')}
                className={`phone-dial-code ${errors.phoneCountry ? 'error' : ''}`}
                {...register("phoneCountry", { required: true })}
                defaultValue=""
              >
                <option value="" disabled></option>
                {COUNTRIES.filter(c => DIAL_CODES[c.code]).map(country => (
                  <option key={country.code} value={country.code}>
                    {DIAL_CODES[country.code]} — {country.name}
                  </option>
                ))}
              </select>
              <span className="phone-dial-code-display" aria-hidden="true">
                {DIAL_CODES[watch("phoneCountry")] || ''}
              </span>
            </div>
            <input
              id="phone"
              type="tel"
              placeholder={t('form.phone')}
              className={errors.phone ? 'error' : ''}
              {...register("phone", {
                required: true,
                pattern: /^\(\d{3}\) \d{3} - \d{4}$/,
              })}
              onChange={formatPhoneNumber}
              onBlur={() => handleBlur("phone")}
              onFocus={() => handleFocus("phone")}
            />
          </div>

          <div className="form-group">
            <select
              id="country"
              className={errors.country ? 'error' : ''}
              {...register("country", { required: true })}
              defaultValue=""
            >
              <option value="" disabled>{t('form.country')}</option>
              {COUNTRIES.map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group checkbox-group">
            <div className="disclaimer-container">
              <div className="disclaimer-text">
                <label className="disclaimer-label">
                  <input
                    type="checkbox"
                    className={errors.disclaimerChecked1 ? 'error' : ''}
                    {...register("disclaimerChecked1", { required: true })}
                  />
                  <span className="disclaimer-label-text">{renderDisclaimerText()}</span>
                </label>
              </div>
            </div>
          </div>

          <button type="submit" className="form-button">
            {t('form.submit')}
          </button>
        </form>
        {/* Only show Shop Now button if NOT onsite */}
        {!isOnsite && (
          <button
            type="button"
            onClick={() => window.open(buildShopUrl(), '_blank')}
            className="shop-now-button"
          >
            {t('form.shopNow')}
          </button>
        )}
          </header>
      </div>
            <Footer />
      <CookieConsent translations={translations} />
    </>
  );
}

export default App;